export type ErrorWithMessage = {
  message: string;
};

export function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

export function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

// https://kentcdodds.com/blog/get-a-catch-block-error-message-with-typescript
export function getErrorMessage(error: unknown) {
  return toErrorWithMessage(error).message;
}

const findFirstOccurrence = (string: string, searchElements: string[], fromIndex = 0) => {
  let min = string.length;
  for (let i = 0; i < searchElements.length; i += 1) {
    const occ = string.indexOf(searchElements[i], fromIndex);
    if (occ !== -1 && occ < min) {
      min = occ;
    }
  }
  return min === string.length ? -1 : min;
};

const getStackItem = (stack: string, step: number = 0) => {
  const targettedStack = step ? stack.split('\n').slice(step).join('\n') : stack;
  const firstCharacter = targettedStack.indexOf('at ') + 3;
  const lastCharacter = findFirstOccurrence(targettedStack, [' ', '\n'], firstCharacter);
  return targettedStack.slice(firstCharacter, lastCharacter);
};

const ignoreList = ['getStackItem', 'logError', 'getStackList'];

const getStackList = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const stack: string = new Error().stack!;
  const stackList: string[] = [];

  let i = 0;
  let item = getStackItem(stack, i);

  do {
    if (!ignoreList.includes(item)) {
      stackList.push(item);
    }
    item = getStackItem(stack, i++);
  } while (item);

  return stackList;
};

export const logError = async (maybeError: unknown) => {
  const error = toErrorWithMessage(maybeError);

  try {
    if (process.env.NODE_ENV !== 'production') {
      const stackList = getStackList();
      // eslint-disable-next-line no-console
      console.error(`Error Stack: `, JSON.stringify(stackList, null, 2));
    }
  } catch (e) {
    /* do nothing */
  }

  return Promise.reject(error);
};
